import React, { useState, useEffect } from 'react';
import { Bell, Globe, Save } from 'lucide-react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import CustomSelect from '../ui/CustomSelect';
import { useLanguage } from '../../context/LanguageContext';

interface ProfileSettingsProps {
  user: {
    id: number;
    email: string;
  };
  onUpdate: (settings: any) => Promise<void>;
  loading: boolean;
}

export default function ProfileSettings({ onUpdate, loading }: ProfileSettingsProps) {
  const { language, setLanguage, t } = useLanguage();
  const [settings, setSettings] = useState({
    email_notifications: true,
    push_notifications: false,
    marketing_emails: false,
    security_alerts: true,
    weekly_digest: true,
    language: language || 'id',
    timezone: 'Asia/Jakarta',
  });

  useEffect(() => {
    setSettings(prev => ({ ...prev, language }));
  }, [language]);

  const handleToggle = (field: string, value: boolean | string) => {
    setSettings(prev => ({ ...prev, [field]: value }));

    // If language is changed, update language context immediately
    if (field === 'language' && typeof value === 'string') {
      setLanguage(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onUpdate(settings);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Notification Settings */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-2 border-b">
          <Bell className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">{t('settings.notifications')}</h3>
        </div>

        <div className="space-y-4 pl-8">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label htmlFor="email_notifications" className="text-base font-medium">
                {t('settings.email_notifications')}
              </Label>
              <p className="text-sm text-gray-500 mt-1">{t('settings.email_notifications_desc')}</p>
            </div>
            <Switch
              id="email_notifications"
              checked={settings.email_notifications}
              onCheckedChange={checked => handleToggle('email_notifications', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label htmlFor="push_notifications" className="text-base font-medium">
                {t('settings.push_notifications')}
              </Label>
              <p className="text-sm text-gray-500 mt-1">{t('settings.push_notifications_desc')}</p>
            </div>
            <Switch
              id="push_notifications"
              checked={settings.push_notifications}
              onCheckedChange={checked => handleToggle('push_notifications', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label htmlFor="security_alerts" className="text-base font-medium">
                {t('settings.security_alerts')}
              </Label>
              <p className="text-sm text-gray-500 mt-1">{t('settings.security_alerts_desc')}</p>
            </div>
            <Switch
              id="security_alerts"
              checked={settings.security_alerts}
              onCheckedChange={checked => handleToggle('security_alerts', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label htmlFor="weekly_digest" className="text-base font-medium">
                {t('settings.weekly_digest')}
              </Label>
              <p className="text-sm text-gray-500 mt-1">{t('settings.weekly_digest_desc')}</p>
            </div>
            <Switch
              id="weekly_digest"
              checked={settings.weekly_digest}
              onCheckedChange={checked => handleToggle('weekly_digest', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label htmlFor="marketing_emails" className="text-base font-medium">
                {t('settings.marketing_emails')}
              </Label>
              <p className="text-sm text-gray-500 mt-1">{t('settings.marketing_emails_desc')}</p>
            </div>
            <Switch
              id="marketing_emails"
              checked={settings.marketing_emails}
              onCheckedChange={checked => handleToggle('marketing_emails', checked)}
            />
          </div>
        </div>
      </div>

      {/* Preferences */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center gap-3 pb-2 border-b">
          <Globe className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">{t('settings.preferences')}</h3>
        </div>

        <div className="space-y-4 pl-8">
          <div className="space-y-2">
            <Label htmlFor="language">{t('settings.language')}</Label>
            <CustomSelect
              value={settings.language}
              onChange={value => handleToggle('language', value)}
              options={[
                { value: 'id', label: 'Bahasa Indonesia' },
                { value: 'en', label: 'English' },
              ]}
              placeholder={t('settings.select_language')}
              width="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">{t('settings.timezone')}</Label>
            <CustomSelect
              value={settings.timezone}
              onChange={value => handleToggle('timezone', value)}
              options={[
                { value: 'Asia/Jakarta', label: 'WIB (Jakarta)' },
                { value: 'Asia/Makassar', label: 'WITA (Makassar)' },
                { value: 'Asia/Jayapura', label: 'WIT (Jayapura)' },
              ]}
              placeholder="Pilih Zona Waktu"
              width="w-full"
            />
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="flex justify-end pt-4 border-t">
        <Button
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Save className="w-4 h-4" />
          {loading ? t('settings.saving') : t('settings.save_settings')}
        </Button>
      </div>
    </form>
  );
}
